import React, { useEffect, useState } from "react";
import styled from "styled-components";
import EyesSVG from "../../assets/svg/eyes.inline.svg";
import useStore from "../../stores/useStore";
import { Link, useStaticQuery, graphql } from "gatsby";
import { GatsbyImage } from "gatsby-plugin-image";
import { gsap } from "gsap";
import Header from "../molecules/Header";

const Holder = styled.div`
  display: none;
  @media (${(props) => props.theme.breakpoints.md}) {
    background-color: #fbdbb4;
    position: fixed;
    z-index: 15000;
    inset: 0;
    display: grid;
    // This is the width of the nav on the desktop header. This is how we line up the titles with the Projects menu item.
    grid-template-columns: 1fr 322px;
    gap: 10px 120px;
    padding: 120px 28px 120px 120px;
    overflow: hidden;
  }

  > :first-child {
    margin-top: 0;
  }
  > :last-child {
    margin-bottom: 0;
  }
`;

const ImageSection = styled.div`
  grid-row: span 2;
  position: relative;
  width: 100%;
  max-width: calc(100lvh - 240px);
  aspect-ratio: 1;
  justify-self: end;
`;

const imageShortEdge = 40; // represents a percentage of the container
const landscapeRatio = 4 / 3;
const portraitRatio = 3 / 4;

const ImageHolder = styled.div`
  position: absolute;
  width: ${imageShortEdge}%;
  height: ${imageShortEdge}%;
  &.landscape {
    width: auto;
    aspect-ratio: ${landscapeRatio};
  }
  &.portrait {
    height: auto;
    aspect-ratio: ${portraitRatio};
  }
  p {
    margin: 0;
  }

  .gatsby-image-wrapper {
    width: 100%;
    height: 100%;
    img {
      width: 100%;
      height: 100%;
      object-fit: cover !important;
      object-position: center;
    }
  }
`;

const NavHolder = styled.div`
  display: flex;
  flex-direction: column;
  gap: 60px;
`;

const Menu = styled.div`
  grid-column: 2/3;
  display: flex;
  flex-direction: column;
  padding-top: 75px;
  opacity: 0;
  transition: opacity 0.3s ease;
  a {
    margin: 0;
    font-size: 22px;
    font-weight: 300;
    line-height: 1.3;
  }
  > :first-child {
    margin-top: 0;
  }
  > :last-child {
    margin-bottom: 0;
  }
`;

const Details = styled.div`
  opacity: 0;
  transition: opacity 0.3s ease;
  grid-column: 2/3;
  p {
    font-size: 17px;
    margin: 0;
    line-height: 1.3;
  }

  .tags-grid {
    display: grid;
  }
`;

const EyesHolder = styled.div`
  position: absolute;
  bottom: 28px;
  left: 28px;
  a {
    display: block;

    svg {
      width: auto;
      height: 24px;
      display: block;
      @media (${(props) => props.theme.breakpoints.md}) {
        width: 77px;
        height: auto;
      }
    }
  }
`;

function ProjectsMenu() {
  const data = useStaticQuery(graphql`
    query {
      prismicHomepage {
        data {
          projects {
            project {
              id
              document {
                ... on PrismicProject {
                  id
                  tags
                  uid
                  data {
                    title {
                      text
                    }
                    menu_image_left
                    menu_image_top
                    menu_image {
                      gatsbyImageData(width: 600)
                    }
                    menu_ratio
                    preview_assets {
                      image {
                        gatsbyImageData(width: 600)
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  `);

  const showProjectsMenu = useStore((state) => state.showProjectsMenu);
  const setShowProjectsMenu = useStore((state) => state.setShowProjectsMenu);
  const [activeProject, setActiveProject] = useState(0);
  const [imagePositions, setImagePositions] = useState([]);

  const projects = data.prismicHomepage.data.projects;

  //

  useEffect(() => {
    // Position images using menu_image_left/top percentages while preventing overflow
    const positions = projects.map((project) => {
      const menuLeft = project?.project?.document.data?.menu_image_left;
      const menuTop = project?.project?.document.data?.menu_image_top;
      const ratio = project.project.document.data.menu_ratio || "Landscape";

      const imageWidth =
        ratio === "Landscape"
          ? imageShortEdge * landscapeRatio
          : imageShortEdge;
      const imageHeight =
        ratio === "Portrait" ? imageShortEdge / portraitRatio : imageShortEdge;

      let left, top;

      if (menuLeft !== null && menuLeft !== undefined) {
        // Convert 0-100 percentage to safe positioning range (0 to 100-imageWidth)
        left = (menuLeft / 100) * (100 - imageWidth);
      } else {
        // Fallback to random positioning
        left = Math.random() * (100 - imageWidth);
      }

      if (menuTop !== null && menuTop !== undefined) {
        // Convert 0-100 percentage to safe positioning range (0 to 100-imageHeight)
        top = (menuTop / 100) * (100 - imageHeight);
      } else {
        // Fallback to random positioning
        top = Math.random() * (100 - imageHeight);
      }

      left = Math.floor(left);
      top = Math.floor(top);

      // console.log(
      //   project.project.document.data.title.text,
      //   menuLeft,
      //   menuTop,
      //   "left: ",
      //   left,
      //   "top: ",
      //   top,
      //   "imageWidth: ",
      //   imageWidth,
      //   "imageHeight: ",
      //   imageHeight,
      //   "imageShortEdge: ",
      //   imageShortEdge
      // );

      return { left, top };
    });
    setImagePositions(positions);
  }, [projects]);

  // Fade in Menu and Details on load
  useEffect(() => {
    if (showProjectsMenu) {
      const menuElement = document.querySelector("[data-menu]");
      const detailsElement = document.querySelector("[data-details]");

      if (menuElement && detailsElement) {
        gsap.set([menuElement, detailsElement], { opacity: 0 });
        gsap.to([menuElement, detailsElement], {
          opacity: 1,
          duration: 0.2,
          ease: "power2.out",
          delay: 0,
        });
      }
    }
  }, [showProjectsMenu]);

  if (!showProjectsMenu) return null;

  //static query here

  const projectsMapped = projects.map((project) => {
    let image = project.project.document.data.menu_image;
    if (image.gatsbyImageData === null) {
      image = project.project.document.data.preview_assets?.[0]?.image || null;
    }
    return {
      title: project.project.document.data.title.text,
      uid: `/project/${project.project.document.uid}`,
      tags: project.project.document.tags ?? [],
      image: image,
      ratio: project.project.document.data.menu_ratio || "Landscape",
    };
  });

  return (
    <Holder>
      <Header isProjects />
      <ImageSection>
        <ImageHolder
          className={projectsMapped[activeProject].ratio.toLowerCase()}
          style={{
            left: imagePositions[activeProject]
              ? `${imagePositions[activeProject].left}%`
              : "0%",
            top: imagePositions[activeProject]
              ? `${imagePositions[activeProject].top}%`
              : "0%",
          }}
        >
          {activeProject === null && <p>Default Image</p>}
          {activeProject !== null && (
            <GatsbyImage
              image={projectsMapped[activeProject].image.gatsbyImageData}
              alt={""}
              objectFit={"contain"}
              loading={"eager"}
            />
          )}
        </ImageHolder>
      </ImageSection>
      <NavHolder>
        <Menu data-menu>
          {projectsMapped.map(({ uid, title }, index) => {
            return (
              <Link
                key={index}
                to={uid}
                onClick={() => setShowProjectsMenu(false)}
                onMouseEnter={() => setActiveProject(index)}
              >
                {title}
              </Link>
            );
          })}
        </Menu>
        <Details data-details>
          {activeProject !== null && (
            <div className="tags-grid">
              {projectsMapped[activeProject].tags?.length > 0
                ? projectsMapped[activeProject].tags.map((tag, i) => (
                    <p key={i}>{tag} </p>
                  ))
                : null}
            </div>
          )}
        </Details>
      </NavHolder>
      <EyesHolder>
        <a
          className="eyes"
          href="https://www.instagram.com/studiolibro/"
          target="_blank"
          rel="noopener noreferrer"
        >
          <EyesSVG />
        </a>{" "}
      </EyesHolder>
    </Holder>
  );
}

export default ProjectsMenu;
